import { useQuery } from '@tanstack/react-query';
import { useStatistics } from '../../../hooks/api/useStatistics';
import { Dayjs } from 'dayjs';
import { useTheme } from '@mui/material/styles';
import { getPatternForColor } from '../utils/chartPatterns';
import { StatsQualityResponse } from '../../../types/Statistics.types';

const QUERY_OPTIONS = {};

const createChartData = (
  data: any[],
  limit: number,
  mapFn: (items: any[]) => any,
  theme?: any
) => ({
  chart: mapFn(data.slice(0, limit)),
  rawData: data,
});

const createSimpleChart = (items: any[], label: string, color: string) => ({
  labels: items.map((item: any) => item.term),
  datasets: [
    {
      label,
      data: items.map((item: any) => item.count),
      backgroundColor: color, // Primary bars don't use patterns
      borderWidth: 0,
    },
  ],
});

const EXCLUDED_CLICK_EVENTS = new Set([
  'search',
  'click',
  'quality',
  'validate-token',
  'calculator-landing',
  'widget-search',
]);

const QUALITY_SYSTEM_LABELS: Record<string, string> = {
  norwegian: 'Norwegian',
  jufo: 'JUFO',
};

const qualityLabel = (system: string) =>
  `Quality (${QUALITY_SYSTEM_LABELS[system] || system})`;

const qualityTotals = (counts: Record<string, number>) =>
  Object.fromEntries(
    Object.entries(counts).map(([system, count]) => [
      qualityLabel(system),
      count,
    ])
  );

const processFiltersData = (
  byDateData: any[],
  qualityData: StatsQualityResponse,
  theme: any
) => {
  if (!Array.isArray(byDateData)) return null;

  const eventMap: Record<string, string> = {
    journal: 'Journal',
    journal_title: 'Journal',
    publishing_models: 'Publishing Models',
    journal_publishing_model: 'Publishing Models',
    payments: 'Payments',
    journal_payment: 'Payments',
    licenses: 'Licenses',
    journal_licenses: 'Licenses',
    agreement_description: 'Agreement description',
  };

  const totals: Record<string, number> = {};

  if (byDateData[0]?.event) {
    byDateData.forEach((item) => {
      if (EXCLUDED_CLICK_EVENTS.has(item.event)) return;

      const displayName = eventMap[item.event] || item.event;
      totals[displayName] = (totals[displayName] || 0) + (item.count || 0);
    });
  } else {
    byDateData.forEach((item) => {
      if (item.journal_title)
        totals['Journal'] = (totals['Journal'] || 0) + item.journal_title;
      if (item.journal_payment)
        totals['Payments'] = (totals['Payments'] || 0) + item.journal_payment;
      if (item.journal_licenses)
        totals['Licenses'] = (totals['Licenses'] || 0) + item.journal_licenses;
      if (item.journal_publishing_model)
        totals['Publishing Models'] =
          (totals['Publishing Models'] || 0) + item.journal_publishing_model;
    });
  }

  const quality = qualityTotals(
    Object.fromEntries(qualityData.map((item) => [item.system, item.count]))
  );
  Object.assign(totals, quality);

  const sortedEntries = Object.entries(totals).sort((a, b) => b[1] - a[1]);
  const labels = sortedEntries.map((entry) => entry[0]);
  const data = sortedEntries.map((entry) => entry[1]);

  return {
    chart: {
      labels,
      datasets: [
        {
          label: 'Column clicks',
          data,
          backgroundColor: theme.palette.warning.main,
          borderWidth: 0,
        },
      ],
    },
    totals: {
      journal: totals['Journal'] || 0,
      payments: totals['Payments'] || 0,
      licenses: totals['Licenses'] || 0,
      publishingModels: totals['Publishing Models'] || 0,
      agreementDescription: totals['Agreement description'] || 0,
      quality,
    },
  };
};

const processSearchFiltersData = (filtersData: any[], theme: any) => {
  if (!Array.isArray(filtersData)) return null;

  const qualityCounts: Record<string, number> = {};

  const filterMap: Record<string, string> = {
    imprint: 'Imprint',
    filter_imprint: 'Imprint',
    publishing_model: 'Publishing Model',
    filter_publishing_model: 'Publishing Model',
    payment: 'Payment',
    filter_payment: 'Payment',
    licenses: 'Licenses',
    filter_licenses: 'Licenses',
  };

  const totals: Record<string, number> = {};

  if (filtersData[0]?.filter) {
    filtersData.forEach((item) => {
      if (item.filter.startsWith('quality_scores_')) {
        const system = item.filter.replace('quality_scores_', '');
        qualityCounts[system] = (qualityCounts[system] || 0) + (item.count || 0);
        return;
      }
      const displayName = filterMap[item.filter] || item.filter;
      totals[displayName] = (totals[displayName] || 0) + (item.count || 0);
    });
  } else {
    filtersData.forEach((item) => {
      if (item.filter_imprint)
        totals['Imprint'] = (totals['Imprint'] || 0) + item.filter_imprint;
      if (item.filter_payment)
        totals['Payment'] = (totals['Payment'] || 0) + item.filter_payment;
      if (item.filter_licenses)
        totals['Licenses'] = (totals['Licenses'] || 0) + item.filter_licenses;
      if (item.filter_publishing_model)
        totals['Publishing Model'] =
          (totals['Publishing Model'] || 0) + item.filter_publishing_model;
    });
  }

  const quality = qualityTotals(qualityCounts);
  Object.assign(totals, quality);

  const sortedEntries = Object.entries(totals).sort((a, b) => b[1] - a[1]);
  const labels = sortedEntries.map((entry) => entry[0]);
  const data = sortedEntries.map((entry) => entry[1]);

  return {
    chart: {
      labels,
      datasets: [
        {
          label: 'Filter usage',
          data,
          backgroundColor: theme.palette.primary.main,
          borderWidth: 0,
        },
      ],
    },
    totals: {
      filterImprint: totals['Imprint'] || 0,
      filterPayment: totals['Payment'] || 0,
      filterLicenses: totals['Licenses'] || 0,
      filterPublishingModel: totals['Publishing Model'] || 0,
      quality,
    },
  };
};

export const useStatisticsQueries = (
  startDate: Dayjs,
  endDate: Dayjs,
  activeTab?: number,
  visibleTabs?: any[]
) => {
  const theme = useTheme();
  const {
    fetchStatisticsTop10,
    fetchStatisticsNoResults,
    fetchStatisticsByDate,
    fetchJournalsByDate,
    fetchFiltersByDate,
    fetchQualityClicks,
    fetchEventsByDate,
    fetchAgreementStatistics,
    fetchStatisticsCollections,
    fetchWidgetOrigins,
  } = useStatistics();

  const dateRange = {
    start: startDate.format('YYYY-MM-DD'),
    end: endDate.format('YYYY-MM-DD'),
  };

  const activeTabKey =
    activeTab !== undefined && visibleTabs
      ? visibleTabs[activeTab]?.queryKey
      : undefined;

  const activityQuery = useQuery({
    queryKey: ['statistics', 'activity', dateRange],
    queryFn: () => fetchStatisticsByDate(dateRange.start, dateRange.end),
    ...QUERY_OPTIONS,
  });

  const topSearchesQuery = useQuery({
    queryKey: ['statistics', 'topSearches', dateRange],
    queryFn: async () => {
      const data = await fetchStatisticsTop10(dateRange.start, dateRange.end);
      return createChartData(data, 10, (items) => ({
        labels: items.map((item: any) => item.term),
        datasets: [
          {
            label: 'Searches',
            data: items.map((item: any) => parseInt(item.count) || 0),
            backgroundColor: theme.palette.primary.main,
            borderWidth: 0,
            xAxisID: 'x',
          },
          {
            label: 'Results',
            data: items.map((item: any) =>
              Math.round(parseFloat(item.avg_results) || 0)
            ),
            backgroundColor: getPatternForColor(
              theme.palette.secondary.main,
              1,
              true
            ), // Add pattern to Results bars
            borderWidth: 0,
            xAxisID: 'x1',
          },
        ],
      }));
    },
    enabled: activeTabKey === 'topSearches',
    ...QUERY_OPTIONS,
  });

  const noResultsQuery = useQuery({
    queryKey: ['statistics', 'noResults', dateRange],
    queryFn: async () => {
      const data = await fetchStatisticsNoResults(
        dateRange.start,
        dateRange.end
      );
      return createChartData(data, 10, (items) =>
        createSimpleChart(items, 'No results count', theme.palette.primary.main)
      );
    },
    enabled: activeTabKey === 'noResults',
    ...QUERY_OPTIONS,
  });

  const clicksQuery = useQuery({
    queryKey: ['statistics', 'clicks', dateRange],
    queryFn: async () => {
      const [data, qualityData] = await Promise.all([
        fetchStatisticsByDate(dateRange.start, dateRange.end),
        fetchQualityClicks(dateRange.start, dateRange.end).catch(() => []),
      ]);
      return processFiltersData(data, qualityData, theme);
    },
    enabled: activeTabKey === 'clicks',
    ...QUERY_OPTIONS,
  });

  const searchFiltersQuery = useQuery({
    queryKey: ['statistics', 'searchFilters', dateRange],
    queryFn: async () => {
      const data = await fetchFiltersByDate(dateRange.start, dateRange.end);
      return processSearchFiltersData(data, theme);
    },
    enabled: activeTabKey === 'searchFilters',
    ...QUERY_OPTIONS,
  });

  const agreementsQuery = useQuery({
    queryKey: ['statistics', 'agreements', dateRange],
    queryFn: async () => {
      const data = await fetchAgreementStatistics(
        dateRange.start,
        dateRange.end
      );
      return createChartData(data, 10, (items) => ({
        labels: items.map((item: any) => item.agreement),
        datasets: [
          {
            label: 'Total clicks',
            data: items.map((item: any) => parseInt(item.clicks) || 0),
            backgroundColor: theme.palette.warning.main,
            borderWidth: 0,
          },
          {
            label: 'Unique journals',
            data: items.map((item: any) => parseInt(item.unique_journals) || 0),
            backgroundColor: getPatternForColor(
              theme.palette.secondary.main,
              1
            ), // Always use pattern for secondary bar
            borderWidth: 0,
          },
        ],
      }));
    },
    enabled: activeTabKey === 'agreements',
    ...QUERY_OPTIONS,
  });

  const journalsQuery = useQuery({
    queryKey: ['statistics', 'journals', dateRange],
    queryFn: async () => {
      const data = await fetchJournalsByDate(dateRange.start, dateRange.end);
      return { journals: data };
    },
    enabled: activeTabKey === 'journals',
    ...QUERY_OPTIONS,
  });

  const accountActivityQuery = useQuery({
    queryKey: ['statistics', 'accountActivity', dateRange],
    queryFn: async () => {
      const data = await fetchEventsByDate(dateRange.start, dateRange.end);

      const dataArray = Array.isArray(data) ? data : [];

      const byAccount: Record<string, { searches: number; clicks: number }> =
        {};

      dataArray.forEach((item) => {
        const accountName = item.account || 'Unknown';

        if (!byAccount[accountName]) {
          byAccount[accountName] = { searches: 0, clicks: 0 };
        }

        const count = item.count || 0;
        if (item.event === 'search') {
          byAccount[accountName].searches += count;
        } else if (item.event === 'journal') {
          byAccount[accountName].clicks += count;
        }
      });

      const allAccounts = Object.entries(byAccount)
        .map(([accountName, data]) => ({
          name: accountName,
          ...data,
          total: data.searches + data.clicks,
        }))
        .filter((item) => item.total > 0)
        .sort((a, b) => b.searches - a.searches);

      const sorted = allAccounts.slice(0, 50);

      return {
        chart: {
          labels: sorted.map((item) => item.name),
          datasets: [
            {
              label: 'Searches',
              data: sorted.map((item) => item.searches),
              backgroundColor: theme.palette.primary.main,
              borderWidth: 0,
            },
            {
              label: 'Clicks',
              data: sorted.map((item) => item.clicks),
              backgroundColor: theme.palette.warning.main,
              borderWidth: 0,
            },
          ],
        },
        totals: {
          totalSearches: allAccounts.reduce(
            (sum, item) => sum + item.searches,
            0
          ),
          totalClicks: allAccounts.reduce((sum, item) => sum + item.clicks, 0),
        },
        events: sorted,
        rawData: allAccounts,
      };
    },
    enabled: activeTabKey === 'accountActivity',
    ...QUERY_OPTIONS,
  });

  const collectionsQuery = useQuery({
    queryKey: ['statistics', 'collections', dateRange],
    queryFn: async () => {
      const data = await fetchStatisticsCollections(
        dateRange.start,
        dateRange.end
      );
      const sorted = [...data].sort((a, b) => b.count - a.count);
      const topCollections = sorted.slice(0, 10);

      return {
        chart: {
          labels: topCollections.map((item) => item.name ?? item.collection),
          datasets: [
            {
              label: 'Count',
              data: topCollections.map((item) => item.count),
              backgroundColor: theme.palette.primary.main,
              borderWidth: 0,
            },
          ],
        },
        rawData: sorted,
      };
    },
    enabled: activeTabKey === 'collections',
    ...QUERY_OPTIONS,
  });

  const widgetOriginsQuery = useQuery({
    queryKey: ['statistics', 'widgetOrigins', dateRange],
    queryFn: async () => {
      const data = await fetchWidgetOrigins(dateRange.start, dateRange.end);
      return { rows: data };
    },
    enabled: activeTabKey === 'widgetOrigins',
    ...QUERY_OPTIONS,
  });

  return {
    activity: activityQuery,
    topSearches: topSearchesQuery,
    noResults: noResultsQuery,
    clicks: clicksQuery,
    searchFilters: searchFiltersQuery,
    agreements: agreementsQuery,
    journals: journalsQuery,
    accountActivity: accountActivityQuery,
    collections: collectionsQuery,
    widgetOrigins: widgetOriginsQuery,
  };
};
